import { FloatingFormItem, InputText } from '@/components/_common';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import {
  useCompanyAvailabilityCreate,
  useCompanyAvailabilityEdit,
} from '@/core/domain/enterprise/company-availability/company-availability.hooks';
import {
  CompanyAvailability,
  CompanyAvailabilityDayOfWeek,
  CompanyAvailabilityDayOfWeekLabel,
} from '@/core/domain/enterprise/company-availability/company-availability.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { DisabledTimes } from '@/types/Form';
import { Modal, Form, Row, Col, Select, Checkbox, TimePicker } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import moment, { Moment } from 'moment';
import { useCallback, useEffect } from 'react';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  disableDayOfWeek?: CompanyAvailabilityDayOfWeek[];
  item?: CompanyAvailability;
}

interface FormValues {
  dayOfWeek: CompanyAvailabilityDayOfWeek;
  allCapacity: boolean;
  capacity: number;
  openingHour?: Moment;
  closingHour?: Moment;
  intervalOpeningHour?: Moment;
  intervalClosingHour?: Moment;
}

export default function CompanyResourceAvaiableFormModal({
  disableDayOfWeek = [],
  item,
  ...props
}: Props) {
  const [form] = Form.useForm<FormValues>();

  const isEdit = !!item;

  const { companyId } = useCompanyFormContext();

  const allCapacity = Form.useWatch('allCapacity', form);
  const closingHour = Form.useWatch('closingHour', form);
  const openingHour = Form.useWatch('openingHour', form);
  const intervalOpeningHour = Form.useWatch('intervalOpeningHour', form);

  const requiredField = useDefaultEmptyFieldError();

  const companyAvailabilityCreate = useCompanyAvailabilityCreate();
  const companyAvailabilityEdit = useCompanyAvailabilityEdit();

  const isLoading =
    companyAvailabilityCreate.isLoading || companyAvailabilityEdit.isLoading;

  const dayOfWeekOptions = Object.values(
    CompanyAvailabilityDayOfWeek,
  ).map<DefaultOptionType>((value) => ({
    label: CompanyAvailabilityDayOfWeekLabel[value],
    value,
    disabled: disableDayOfWeek
      .filter((day) => !isEdit || day !== item.dayOfWeek)
      .includes(value),
  }));

  useEffect(() => {
    if (item) {
      form.setFieldsValue({
        ...item,
        allCapacity: item.capacity === 0,
        capacity: item.capacity === 0 ? undefined : item.capacity,
        openingHour: moment(item.openingHour, 'HH.mm'),
        closingHour: moment(item.closingHour, 'HH.mm'),
        intervalOpeningHour: item.intervalOpeningHour
          ? moment(item.intervalOpeningHour, 'HH.mm')
          : undefined,
        intervalClosingHour: item.intervalClosingHour
          ? moment(item.intervalClosingHour, 'HH.mm')
          : undefined,
      });
    }
  }, [item]);

  const handleSubmit = async () => {
    try {
      if (!companyId) return;

      const { allCapacity, ...values } = await form.validateFields();

      const payload = {
        capacity: allCapacity ? 0 : values.capacity,
        openingHour: values.openingHour?.format('HH.mm') as string,
        closingHour: values.closingHour?.format('HH.mm') as string,
        intervalOpeningHour: values.intervalOpeningHour?.format(
          'HH.mm',
        ) as string,
        intervalClosingHour: values.intervalClosingHour?.format(
          'HH.mm',
        ) as string,
      };

      if (isEdit) {
        await companyAvailabilityEdit.mutateAsync({
          ...values,
          ...payload,
          id: item.id,
        });
      } else {
        await companyAvailabilityCreate.mutateAsync({
          ...values,
          ...payload,
          companyId,
        });
      }

      handleClose();
    } catch {}
  };

  const handleClose = useCallback(() => {
    if (isLoading) return;

    form.resetFields();
    props.onClose();
  }, [isLoading]);

  const disableOpeningHour = useCallback((): DisabledTimes => {
    const _hour = closingHour?.clone().hour();

    const disabledHours = () => {
      if (typeof _hour !== 'number') return [];

      return Array.from({ length: 24 - _hour }).map(
        (_, index) => index + _hour,
      );
    };

    return {
      disabledHours,
    };
  }, [closingHour]);

  const disableClosingHour = useCallback((): DisabledTimes => {
    const _hour = openingHour?.clone().hour();

    const disabledHours = () => {
      if (typeof _hour !== 'number') return [];

      return Array.from({ length: _hour + 1 }).map((_, index) => index);
    };

    return {
      disabledHours,
    };
  }, [openingHour]);

  const disableIntervalOpeningHour = useCallback((): DisabledTimes => {
    const _startHour = openingHour?.clone().hour();
    const _endHour = closingHour?.clone().hour();

    const disabledHours = () => {
      if (typeof _startHour !== 'number' || typeof _endHour !== 'number')
        return [];

      const startHours = Array.from({ length: _startHour + 1 }).map(
        (_, index) => index,
      );

      const endHours = Array.from({ length: 24 - _endHour }).map(
        (_, index) => _endHour + index,
      );

      return [...startHours, ...endHours];
    };

    return {
      disabledHours,
    };
  }, [openingHour, closingHour]);

  const disableIntervalClosingHour = useCallback((): DisabledTimes => {
    const _startHour = intervalOpeningHour?.clone().hour();
    const _endHour = closingHour?.clone().hour();

    const disabledHours = () => {
      if (typeof _startHour !== 'number' || typeof _endHour !== 'number')
        return [];

      const startHours = Array.from({ length: _startHour + 1 }).map(
        (_, index) => index,
      );

      const endHours = Array.from({ length: 24 - _endHour }).map(
        (_, index) => _endHour + index,
      );

      return [...startHours, ...endHours];
    };

    return {
      disabledHours,
    };
  }, [intervalOpeningHour, closingHour]);

  return (
    <Modal
      title={`${isEdit ? 'Atualizar' : 'Adicionar'} disponibilidade da semana`}
      open={props.opened}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={isEdit ? 'Atualizar' : 'Cadastrar'}
      confirmLoading={isLoading}
      maskClosable={!isLoading}
      closable={!isLoading}
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label="Dia da semana"
              name="dayOfWeek"
              rules={[requiredField]}
            >
              <Select
                style={{ width: '100%' }}
                options={dayOfWeekOptions}
                disabled={isLoading}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Form.Item
              noStyle
              name="allCapacity"
              valuePropName="checked"
              initialValue={false}
              getValueFromEvent={(value) => {
                if (value.target.checked) {
                  form.setFields([{ name: 'capacity', errors: [] }]);
                }

                return value.target.checked;
              }}
            >
              <Checkbox style={{ marginBottom: 16 }} disabled={isLoading}>
                Todos os recursos disponíveis
              </Checkbox>
            </Form.Item>

            <FloatingFormItem
              label="Recursos disponíveis"
              name="capacity"
              rules={allCapacity ? [] : [requiredField]}
            >
              <InputText
                onlyNumber
                min={0}
                style={{ width: '100%' }}
                disabled={allCapacity || isLoading}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <strong>Horário de funcionamento</strong>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <FloatingFormItem
                  label="Horário de abertura"
                  name="openingHour"
                  rules={[requiredField]}
                >
                  <TimePicker
                    format="HH:mm"
                    showSecond={false}
                    showNow={false}
                    placeholder=""
                    style={{ width: '100%' }}
                    disabledTime={disableOpeningHour}
                    disabled={isLoading}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  label="Horário de fechamento"
                  name="closingHour"
                  rules={[requiredField]}
                >
                  <TimePicker
                    format="HH:mm"
                    showSecond={false}
                    showNow={false}
                    placeholder=""
                    style={{ width: '100%' }}
                    disabledTime={disableClosingHour}
                    disabled={!openingHour || isLoading}
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <strong>Horário de intervalo</strong>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <FloatingFormItem
                  label="Início do intervalo"
                  name="intervalOpeningHour"
                  getValueFromEvent={(value) => {
                    if (!value) {
                      form.setFieldValue('intervalClosingHour', undefined);
                    }
                    return value;
                  }}
                >
                  <TimePicker
                    format="HH:mm"
                    showSecond={false}
                    showNow={false}
                    placeholder=""
                    style={{ width: '100%' }}
                    disabledTime={disableIntervalOpeningHour}
                    disabled={!openingHour || !closingHour || isLoading}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  label="Fim do intervalo"
                  name="intervalClosingHour"
                  rules={[
                    {
                      type: 'date',
                      validator: (_, value) => {
                        const intervalOpeningHour = form.getFieldValue(
                          'intervalOpeningHour',
                        ) as Moment;

                        if (!value && intervalOpeningHour?.isValid()) {
                          return Promise.reject('Informe o fim do intervalo');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    showSecond={false}
                    showNow={false}
                    placeholder=""
                    style={{ width: '100%' }}
                    disabledTime={disableIntervalClosingHour}
                    disabled={!intervalOpeningHour || isLoading}
                  />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
